import { lazy, Suspense } from 'react'
import { isMobile } from 'react-device-detect'
import { createPortal } from 'react-dom'
import ReactDOM from 'react-dom/client'
import { ErrorBoundary } from 'react-error-boundary'
import { pipe } from 'remeda'
import { CartDrawer } from '@components/CartDrawer/CartDrawer'
import Header from '@components/Header'
import renderLiquidLinks from '@components/Header/utils/renderLiquidLinks'
import { WishlistDrawer } from '@components/WishlistDrawer/WishlistDrawer'
import {
  withApollo,
  withErrorBoundary,
  withExchangeRates,
  withI18next,
  withStaffPickImage,
  withTheme,
} from '@hocs'
import type { CartDrawerData } from '@velasca/types'
import { ErrorFallback } from '@velasca/ui/error-fallback'
import { productMedia } from '@velasca/utils/globals'
import { parseJSONSafe } from '@velasca/utils/parse-json-safe'
import { transformProductMedia } from '@velasca/utils/transform-product-media'
import { withWishlist } from '../hocs/withWishlist'
import { getBreadcrumbs } from './FooterApp'

const GiftCardProductPage = lazy(
  () => import('@components/GiftCardProductPage/GiftCardProductPage'),
)

const giftCardEl = document.getElementById('gift-card-product')
const giftCardProductJson = document.getElementById('gift-card-product-json')
const giftCardSizesEl = document.getElementById('gift-card-sizes-json')
const giftCardInstructionsEl = document.getElementById(
  'gift-card-instructions-json',
)
const productMoreDetailsEl = document.getElementById(
  'product-gift-card-more-details-json',
)

export const renderGiftCardPageApp = (
  cartDrawerData: CartDrawerData,
  headerEl: HTMLElement | null,
  headerData: any,
  promotionalBannerData: any,
  minisearchData: any,
) => {
  if (giftCardEl && giftCardProductJson) {
    const breadcrumbs = getBreadcrumbs(headerData?.nav)
    const GiftCardPageApp = () => (
      <>
        <CartDrawer
          emptyCartCtas={cartDrawerData?.ctas || []}
          editorialSuggestedProductsDesktopImage={
            cartDrawerData?.editorial?.suggestedProductsDesktopImage
          }
        />
        <WishlistDrawer />
        {headerEl &&
          createPortal(
            <Header
              sticky
              promotionalBanner={promotionalBannerData || {}}
              miscHeaderData={headerData?.miscData}
              minisearch={minisearchData}
              lowerMenu={headerData?.mobileOnlyNav}
              menu={
                renderLiquidLinks(
                  headerData?.nav,
                  headerData?.miscData?.liquidLinks,
                ) ?? undefined
              }
            />,
            headerEl,
          )}
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <Suspense fallback={null}>
            <GiftCardProductPage
              breadcrumbs={breadcrumbs}
              product={parseJSONSafe(giftCardProductJson.innerText)}
              productMedia={transformProductMedia(
                productMedia as any,
                isMobile,
              )}
              sizesWithDescriptions={parseJSONSafe(
                giftCardSizesEl?.innerText || '',
              )}
              instructions={parseJSONSafe(
                giftCardInstructionsEl?.innerText || '',
              )}
              moreDetailsInfo={
                productMoreDetailsEl?.innerText
                  ? parseJSONSafe(productMoreDetailsEl.innerText)
                  : { description: '' }
              }
              recentProductsSettings={{
                enabled: true,
              }}
            />
          </Suspense>
        </ErrorBoundary>
      </>
    )

    const GiftCardPageAppWrapper = pipe(
      GiftCardPageApp,
      withStaffPickImage,
      withExchangeRates,
      withWishlist,
      withI18next,
      withApollo,
      withErrorBoundary,
      withTheme,
    )

    ReactDOM.createRoot(giftCardEl).render(<GiftCardPageAppWrapper />)
  }
}
